import * as hooks from "lib/hooks"
import { MCCModal, Sidebar } from "components"
import * as valid from "lib/valid"
import { EventMessage, EventType, InteractionStatus } from "@azure/msal-browser"
import * as opt from "lib/opt"
import {
	Alert,
	Box,
	Button,
	DialogProps,
	Divider,
	Snackbar,
	SxProps,
	Theme,
	Typography,
} from "@mui/material"
import {
	ActionCreatorWithoutPayload,
	ActionCreatorWithPayload,
	createSelector,
	PayloadAction,
} from "@reduxjs/toolkit"

import {
	createContext,
	Dispatch,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { UnknownAction } from "redux"
import {
	ActionCreatorsFromHandlers,
	ContainmentsSlice,
	GetSlice,
	RootState,
} from "store"

import { Draft } from "immer"
import { Result } from "lib/result"

import { useBlocker, useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useImmerReducer } from "use-immer"
import { useMsal } from "@azure/msal-react"
import { SnackProps, UseFileDownloadOpts } from "lib/hooks"
import { Containment } from "models"

export function UseSubscriptionWithoutObs<T>(
	initialValue: T,
	cb: (value: T) => void,
	{ runOnFirstRender = false } = {}
): (value: T) => void {
	const cbRef = useRef(cb);
	const [value, setValue] = useState<T>(initialValue);

	// update cb ref if it changes
	useEffect(() => {
		cbRef.current = cb;
	}, [cb]);

	// run callback on first render if flag is true
	useEffect(() => {
		if (runOnFirstRender) {
			cbRef.current(initialValue);
		}
	}, [runOnFirstRender, initialValue]);

	// run callback when value changes
	useEffect(() => {
		cbRef.current(value);
	}, [value]);

	return setValue;
}

interface snackCtxProps {
	snackBar: SnackProps
	open: (r: Result<string | SnackProps, string>) => void
}

const defaultSnackProps: SnackProps = {
	open: false,
	Message: "",
	Severity: "success",
	AutoHideDuration: 4000,
}
export const SnackBarCtx = createContext<snackCtxProps>({
	snackBar: defaultSnackProps,
	open: () => { },
})

export function SnackBarProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const [snackBar, setSnackBar] = useState<SnackProps>(defaultSnackProps)

	const onClose = () => {
		setSnackBar((p) => ({ ...p, open: false }))
	}
	const open = useCallback(
		(result: Result<string | SnackProps, SnackProps | string>) => {
			const snackProps = result.Match({
				Ok: (message) => {
					if (typeof message === "string") {
						return { open: true, Message: message, Severity: "success" }
					}
					return { ...defaultSnackProps, ...message }
				},
				Err: (message) => {
					if (typeof message === "string") {
						return { open: true, Message: message, Severity: "error" }
					}
					return { ...defaultSnackProps, ...message }
				},
			})

			setSnackBar((p) => p.Merge(snackProps))
		},
		[setSnackBar]
	)

	return (
		<SnackBarCtx.Provider value={{ snackBar, open }}>
			{children}
			<Snackbar
				open={snackBar.open}
				autoHideDuration={snackBar.AutoHideDuration}
				onClose={onClose}
			>
				<Alert
					onClose={onClose}
					severity={snackBar.Severity}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{snackBar.Message}
				</Alert>
			</Snackbar>
		</SnackBarCtx.Provider>
	)
}

export const UseSnackBar = (): snackCtxProps => useContext(SnackBarCtx)

export interface ModalProps {
	isOpen: bool
	onClose: () => void
	onOpen: (p: ModalOpenProps) => void
}

export const ModalCtx = createContext<ModalProps>({
	isOpen: false,
	onClose: () => { },
	onOpen: () => { },
})

export interface ModalOpenProps {
	title: React.JSX.Element | string
	onConfirm?: () => void
	onConfirmText?: string
	hideConfirm?: bool
	onCancelText?: string
	onCancel?: () => void
	hideCancel?: bool
	dialogProps?: Partial<DialogProps>
	content: React.ComponentType | string | React.JSX.Element
	actions?: React.ReactNode
	// Optional styling overrides
	cancelButtonSx?: Record<string, any>
	confirmButtonSx?: Record<string, any>
	dialogActionsSx?: Record<string, any>
	// New property to determine display type
	kind?: "modal" | "sidebar"
	// Sidebar specific properties
	sidebarWidth?: number | string
	sidebarPosition?: "left" | "right"
	hideCloseButton?: boolean
	preventClose?: bool
}

export function ModalProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const { theme } = hooks.UseTheme()
	const [open, setOpen] = useState(false)
	const [modalDetails, setModalDetails] = hooks.UseOption<ModalOpenProps>()

	const onClose = useCallback(() => {
		setOpen(false)
	}, [setOpen])

	const onOpen = useCallback(
		(props: ModalOpenProps) => {
			setOpen(true)
			setModalDetails(
				opt.Some({
					...props,
					kind: props.kind || "modal", // Default to modal if not specified
					sidebarPosition: props.sidebarPosition || "right",
					sidebarWidth: props.sidebarWidth !== undefined ? props.sidebarWidth : 350,
				})
			)
		},
		[setOpen, setModalDetails]
	)

	const renderContent = () => {
		return modalDetails
			.IfSome((modalDetails) => {
				if (valid.IsString(modalDetails.content)) {
					return (
						<Typography variant="body1" color="text.secondary">
							{modalDetails.content}
						</Typography>
					)
				}

				/* eslint-disable-next-line */
				const Content = modalDetails.content
				if (IsReactComponent(Content)) {
					return <Content />
				}
				return Content
			})
			.UnwrapOrNil()
	}

	// Render sidebar content with appropriate actions
	const renderSidebarContent = (details: ModalOpenProps) => {
		/* eslint-disable-next-line */
		const Content = details.content

		return (
			<>
				<Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
					{valid.IsString(Content) ? (
						<Typography variant="body1">{Content}</Typography>
					) : IsReactComponent(Content) ? (
						<Content />
					) : (
						Content
					)}
				</Box>

				{!details.actions && (
					<>
						<Divider />
						<Box
							sx={{
								p: 2,
								display: "flex",
								justifyContent: "flex-end",
								gap: 1.5,
								bgcolor: theme.palette.background.paper,
								borderTop: `1px solid ${theme.palette.divider}`,
								position: "sticky",
								bottom: 0,
								zIndex: 1,
							}}
						>
							{!details.hideCancel && (
								<Button
									onClick={() => {
										onClose()
										details?.onCancel?.()
									}}
									variant="outlined"
									color="primary"
									sx={{
										borderRadius: "4px",
										textTransform: "none",
										fontWeight: 500,
										flex: 1,
										py: 1,
										borderWidth: 1.5,
										...(details.cancelButtonSx || {}),
									}}
								>
									{details.onCancelText ?? "Cancel"}
								</Button>
							)}
							{!details.hideConfirm && (
								<Button
									onClick={() => {
										details.onConfirm?.()
										onClose()
									}}
									variant="contained"
									color="primary"
									autoFocus
									sx={{
										"borderRadius": "4px",
										"textTransform": "none",
										"fontWeight": 500,
										"flex": 1,
										"py": 1,
										"boxShadow": "0 2px 6px rgba(0, 0, 0, 0.1)",
										"&:hover": {
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
										},
										...(details.confirmButtonSx || {}),
									}}
								>
									{details.onConfirmText ?? "Confirm"}
								</Button>
							)}
						</Box>
					</>
				)}
			</>
		)
	}

	// Style for the main content that pushes it aside when sidebar is open
	const mainContentStyle = modalDetails
		.IfSome((details) => {
			if (details.kind === "sidebar" && open) {
				const sidebarPosition = details.sidebarPosition || "right"
				const sidebarWidth = details.sidebarWidth !== undefined ? details.sidebarWidth : 350

				return {
					transition: theme.transitions.create(["margin"], {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.enteringScreen,
					}),
					[`margin${sidebarPosition === "left" ? "Left" : "Right"}`]:
						typeof sidebarWidth === "number" ? `${sidebarWidth}px` : sidebarWidth,
				}
			}
			return {}
		})
		.UnwrapOr({})

	// Calculate width based on dialogProps.maxWidth or default
	const getModalWidth = (details: ModalOpenProps): number | string => {
		const paperProps = details.dialogProps?.PaperProps as PaperPropsWithSx | undefined
		if (paperProps?.sx?.width) {
			return paperProps.sx.width
		}

		switch (details.dialogProps?.maxWidth) {
			case "xs":
				return 444
			case "sm":
				return 600
			case "md":
				return 900
			case "lg":
				return 1200
			case "xl":
				return 1536
			default:
				return 500
		}
	}

	return (
		<ModalCtx.Provider value={{ onOpen, onClose, isOpen: open }}>
			<Box sx={mainContentStyle}>{children}</Box>

			{modalDetails
				.IfSome((modalDetails) => {
					// Render sidebar when kind is "sidebar"
					if (modalDetails.kind === "sidebar") {
						return (
							<Sidebar
								open={open}
								onClose={() => {
									if (!modalDetails.preventClose) {
										onClose()
									}
									modalDetails?.onCancel?.()
								}}
								width={modalDetails.sidebarWidth || "350px"}
								position={modalDetails.sidebarPosition || "right"}
								hideCloseButton={modalDetails.hideCloseButton}
								title={modalDetails.title}
							>
								{renderSidebarContent(modalDetails)}
							</Sidebar>
						)
					}

					// Modal rendering
					return (
						<MCCModal
							open={open}
							onClose={() => {
								if (!modalDetails.preventClose) {
									onClose()
								}
								modalDetails?.onCancel?.()
							}}
							title={modalDetails.title}
							width={getModalWidth(modalDetails)}
							cancelText={modalDetails.onCancelText || "Cancel"}
							confirmText={modalDetails.onConfirmText || "Confirm"}
							onConfirm={() => {
								modalDetails.onConfirm?.()
								onClose()
							}}
						>
							{renderContent()}
						</MCCModal>
					)
				})
				.UnwrapOrNil()}
		</ModalCtx.Provider>
	)
}

export interface PaperPropsWithSx {
	sx?: SxProps<Theme> & {
		width?: number | string
		zIndex?: number
	}
}

export function UseModal(): ModalProps {
	return useContext(ModalCtx)
}

export function UseLatest<T extends any>(value: T) {
	const ref = useRef<T>(value)
	ref.current = value
	return ref
}

export function UseUnsavedChanges(hasUnsavedChanges: bool | (() => bool), onConfirm: () => void) {
	const modal = UseModal()
	const navigate = useNavigate()
	const onConfirmRef = UseLatest(onConfirm)

	const handleBlockedNavigation = useCallback(() => {
		return new Promise<bool>((resolve) => {
			modal.onOpen({
				title: "Unsaved Changes",
				content: "You have unsaved changes. Are you sure you want to leave?",
				onConfirm: () => {
					onConfirmRef.current()
					modal.onClose()
					resolve(true)
				},
				onCancel: () => {
					modal.onClose()
					resolve(false)
				},
				onConfirmText: "Leave",
				onCancelText: "Stay",
			})
		})
	}, [modal])
	const currRoute = hooks.UseCurrentRoute()

	const blocker = useBlocker(
		useCallback(
			(tx: { nextLocation: { pathname: string } }) => {
				const unsavedChanged = valid.IsFunc(hasUnsavedChanges)
					? hasUnsavedChanges()
					: hasUnsavedChanges
				if (
					unsavedChanged &&
					//prevent blocker from blocking navigation to the same route
					currRoute
						.IfSome((r) => r.Path !== tx.nextLocation.pathname)
						.Expect("Route not found")
				) {
					handleBlockedNavigation().then((proceed) => {
						if (proceed) {
							navigate(tx.nextLocation.pathname)
						}
					})
					return true // Always block initially
				}
				return false // Allow navigation if no unsaved changes
			},
			[hasUnsavedChanges, handleBlockedNavigation]
		)
	)

	useEffect(() => {
		if (blocker.state === "blocked" && !hasUnsavedChanges) {
			blocker.reset()
		}
	}, [blocker, hasUnsavedChanges])

	return blocker
}

export function UseAction<T extends any[], R extends UnknownAction>(
	fn: (...args: T) => R,
	_dispatch?: Dispatch<R>
) {
	const dispatch = useDispatch()
	return useCallback(
		(...args: T) => {
			(_dispatch ?? dispatch)(fn(...args))
		},
		[dispatch, _dispatch]
	)
}

type actionCreator = ActionCreatorWithPayload<any, any> | ActionCreatorWithoutPayload
type payloadFromActionCreator<T extends actionCreator> =
	T extends ActionCreatorWithPayload<infer P, any>
	? P
	: T extends ActionCreatorWithoutPayload
	? void
	: never

// Helper type for function signature based on action creator
type actionFunc<T extends actionCreator> =
	payloadFromActionCreator<T> extends void
	? () => void
	: (payload: payloadFromActionCreator<T>) => void

type actionsReturn<T extends Record<string, actionCreator>> = {
	[K in keyof T]: actionFunc<T[K]>
}

export function UseActions<T extends Record<string, actionCreator>, D extends Dispatch<any>>(
	actions: T,
	_dispatch?: D extends Dispatch<infer A> ? Dispatch<A> : Dispatch<UnknownAction>
): actionsReturn<T> {
	const dispatchRef = UseLatest(_dispatch)
	const dispatch = useDispatch()

	return useMemo(
		(): actionsReturn<T> =>
			actions.Entries().Fold({} as actionsReturn<T>, (acc, [key, actionCreator]) => {
				acc[key as keyof T] = ((payload) => {
					(dispatchRef.current ?? dispatch)(actionCreator(payload))
				}) as actionFunc<T[keyof T]>

				return acc
			}),
		[dispatch, actions]
	)
}

export function UseContainments(): Containment[] {
	return useSelector(ContainmentsSlice.selectors.selectContainments)
}

export function UseReduxSetState<S, R>(
	selector: (s: S) => R,
	action: ActionCreatorWithPayload<R>
): [state: R, setState: (state: R) => void] {
	const state = useSelector(selector)
	const setState = UseAction(action)
	return [state, setState]
}

export function UseMsalCallbacks(): (
	eventMatcher: evtMatcher,
	cb: (event: EventMessage) => void
) => void {
	const callbackIds = useRef<(string | null)[]>([])
	const { instance, inProgress } = useMsal()

	const addCallback = useCallback(
		(eventMatcher: evtMatcher, cb: (event: EventMessage) => void) => {
			callbackIds.current.Append(
				instance.addEventCallback((event) => {
					if (
						(valid.IsFunc(eventMatcher)
							? eventMatcher(event.eventType)
							: eventMatcher === event.eventType) &&
						inProgress === InteractionStatus.None
					) {
						cb(event)
					}
				})
			)
		},
		[instance, inProgress]
	)

	useEffect(() => {
		return () => {
			callbackIds.current.Range((_, id) => {
				if (id) {
					instance.removeEventCallback(id)
				}
			})
		}
	}, [instance])

	return addCallback
}

// Overloads
export function UseNullHook<TResult, Args extends [any, ...any[]]>(
	hook: (...args: Args) => TResult | nil,
	...args: Args
): opt.Opt<TResult>

// Overload for hooks without arguments (Less specific)
export function UseNullHook<TResult>(hook: () => TResult | nil): opt.Opt<TResult>

// Implementation
export function UseNullHook<TResult>(
	hook: (...args: any[]) => TResult | nil,
	...args: any[]
): opt.Opt<TResult> {
	const result = hook(...args)

	return useMemo<opt.Opt<TResult>>(() => opt.FromNullable(result), [result])
}

export function UseValueWatcher<TInput>() {
	const currentTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const currentAbort = useRef<AbortController | null>(null);

	const watch = useCallback(
		(
			val: TInput,
			runEffect: (val: TInput, signal: AbortSignal) => Promise<void> | void,
			delay: number
		) => {
			if (currentTimeout.current) {
				clearTimeout(currentTimeout.current);
			}
			if (currentAbort.current) {
				currentAbort.current.abort();
			}

			const abort = new AbortController();
			currentAbort.current = abort;

			currentTimeout.current = setTimeout(() => {
				runEffect(val, abort.signal);
			}, delay); // <-- Now uses the correct delay
		},
		[]
	);

	const clear = useCallback(() => {
		if (currentTimeout.current) {
			clearTimeout(currentTimeout.current);
		}
		if (currentAbort.current) {
			currentAbort.current.abort();
		}
	}, []);

	return { watch, clear };
}


// usage:
// const valueWatcher = UseValueWatcher<int>();
//
// useEffect(() => {
//   valueWatcher.watch(
//     4,
//     (v) =>
//       rx.interval(1000).pipe(
//         rx.map((intervalCt) => intervalCt + v),
//         rx.tap((count) => console.log('count', count)),
//       ),
//   );
// }, [valueWatcher.watch]);

type evtMatcher = EventType | ((eventType: EventType) => bool)

export function UseLocalStorageState<T>(
	key: string,
	initialValue: T
): [T, (v: T | ((prev: T) => T)) => void] {
	const getInitialValue = (): T => {
		try {
			const stored = localStorage.getItem(key)
			return stored ? JSON.parse(stored) : initialValue
		} catch (e) {
			log.Warn("error parsing local storage")
			return initialValue
		}
	}

	const [value, setValue] = useState<T>(getInitialValue)

	const set = useCallback(
		(next: T | ((prev: T) => T)) => {
			setValue((prev) => {
				const newValue = typeof next === "function" ? (next as (prev: T) => T)(prev) : next
				localStorage.setItem(key, JSON.stringify(newValue))
				return newValue
			})
		},
		[key]
	)

	return [value, set]
}


// No params version
export function UseTypedSelector<S, R>(
	getSlice: GetSlice<S>,
	selector: (root: RootState, slice: S) => R
): R

// Single param version
export function UseTypedSelector<S, P, R>(
	getSlice: GetSlice<S>,
	selector: (root: RootState, slice: S, param: P) => R,
	param: P
): R

// Multiple params version
export function UseTypedSelector<S, P extends any[], R>(
	getSlice: GetSlice<S>,
	selector: (root: RootState, slice: S, ...params: P) => R,
	...params: P
): R

// Implementation
export function UseTypedSelector<S, P extends any[], R>(
	getSlice: GetSlice<S>,
	selector: ((root: RootState, slice: S) => R) | ((root: RootState, slice: S, ...params: P) => R),
	...params: P
): R {
	const memoizedSelector = useMemo(() => {
		if (params.length > 0) {
			return createSelector(
				[
					(state: RootState) => state,
					getSlice,
					...params.Map((_, index) => (_: any) => params[index]),
				],
				(state, slice, ...parameters) =>
					(selector as (root: RootState, slice: S, ...params: P) => R)(
						state,
						slice,
						...(parameters as P)
					)
			)
		}
		return createSelector([(state: RootState) => state, getSlice], (state, slice) =>
			(selector as (root: RootState, slice: S) => R)(state, slice)
		)
	}, [getSlice, selector, ...params])

	return useSelector(memoizedSelector)
}

type compareFn<T> = (a: T, b: T) => bool
export function UseMemoedDependency<T>(val: T, fn: compareFn<T>): T {
	const old = useRef(val)
	if (!fn(old.current, val)) {
		old.current = val
	}
	return old.current
}

function createImmerAction<TState, TPayload>(
	type: string,
	reducer: (draft: Draft<TState>, action: PayloadAction<TPayload>) => void
) {
	const actionCreator = (payload: TPayload) => ({ type, payload })
		; (actionCreator as any).reducer = reducer
		; (actionCreator as any).type = type
		; (actionCreator as any).match = (action: PayloadAction<any>) => action.type === type
	return actionCreator as ActionCreatorWithPayload<TPayload, string> & {
		reducer: typeof reducer
	}
}

// lets you pass state and actions handlers to and will return the state and bound actions
// so you dont need to wrap with dispatch

export function UseMutableReducer<
	TState,
	THandlers extends Record<string, (draft: Draft<TState>, action: PayloadAction<any>) => void>,
>(initialState: TState, handlers: THandlers) {
	const handlersRef = UseLatest(handlers)

	const actions = useMemo(() => {
		return handlersRef.current
			.Entries()
			.Fold({} as ActionCreatorsFromHandlers<THandlers>, (acc, [key, handler]) => {
				acc[key as keyof THandlers] = createImmerAction(
					key as string,
					handler
				) as ActionCreatorsFromHandlers<THandlers>[keyof THandlers]
				return acc
			})
	}, [])

	const [state, dispatch] = useImmerReducer<TState, PayloadAction<any>>((draft, action) => {
		for (const key in actions) {
			const matchedAction = actions[key as keyof typeof actions]
			if (matchedAction.match(action)) {
				(matchedAction as any).reducer(draft, action)
				return
			}
		}
	}, initialState)

	const boundActions = UseActions(actions, dispatch)

	return [state, boundActions] as const
}

type getMapKey<T extends Map<any, any>> = T extends Map<infer K, any> ? K : never
type getMapVal<T extends Map<any, any>> = T extends Map<any, infer V> ? V : never
export function UseMap<
	T extends Map<string, any>,
	K extends getMapKey<T> = getMapKey<T>,
	V extends getMapVal<T> = getMapVal<T>,
>(initialState: T) {
	const state = useRef(initialState)
	const remove = (key: K) => {
		state.current.delete(key)
	}

	const set = (key: K, value: V) => {
		state.current.set(key, value)
	}

	const get = (key: K, namespace?: string): V => {
		const out = state.current.get(key)
		if (namespace && out) {
			return out.Replace(`${namespace}.`, "")
		}
		return out
	}

	return { state, remove, set, get } as const
}

export function IsReactComponent(
	component: React.ComponentType | string | React.JSX.Element
): component is React.ComponentType {
	return component instanceof Function
}

type stateWithGet<T> = T & {
	get: <K extends keyof T>(key: K) => Opt<T[K]>
}

export function UseRecord<T extends Record<string, any>>(
	initialState: T
): [state: stateWithGet<T>, setState: (s: Partial<T>) => void] {
	const [state, setState] = useImmerReducer<stateWithGet<T>, Partial<T>>(
		(...args) => {
			Object.assign(...args)
		},
		{
			...initialState,
			get<K extends keyof T>(this: T, key: K): Opt<T[K]> {
				return opt.FromNullable(this[key])
			},
		}
	)

	return [state, setState]
}

// helper to reduce boilerplate for using options
export function UseOption<T>(
	initialState?: T | nil | Opt<T>
): [Opt<T>, (value?: SetStateAction<Opt<T>> | T) => void] {
	const [state, setState] = useState<Opt<T>>(
		initialState instanceof opt.Opt ? initialState : opt.FromNullable(initialState)
	)

	const setter = useCallback(
		(value?: SetStateAction<Opt<T>> | T) => {
			if (isNil(value)) {
				setState(opt.None())
				return
			}

			if (value instanceof Function) {
				setState(value)
				return
			}

			if (value instanceof opt.Opt) {
				setState(value)
				return
			}

			setState(opt.Some(value as T))
		},
		[setState]
	)

	return [state, setter]
}

export function UseOutsideClick(ref: React.RefObject<HTMLElement | null>, cb: () => void) {
	useEffect(() => {
		function handleClick(e: MouseEvent) {
			const target = e.target as HTMLElement
			const isSelectMenu =
				target.closest(".MuiPopover-root") ||
				target.closest(".MuiSelect-select") ||
				target.closest(".MuiMenuItem-root") ||
				target.closest(".MuiBackdrop-root")

			if (ref.current && !ref.current.contains(target) && !isSelectMenu) {
				cb()
			}
		}
		document.addEventListener("mousedown", handleClick)
		return () => {
			document.removeEventListener("mousedown", handleClick)
		}
	}, [ref, cb])
}

export function UseFileDownload({
	fileFactory,
	fileName,
	onResult,
	autoTrigger,
}: UseFileDownloadOpts): () => Promise<void> {
	const triggerDownload = useCallback(async () => {
		try {
			const blob = await fileFactory();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", fileName);
			document.body.appendChild(link);
			link.click();

			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				document.body.removeChild(link);
			}, 100);

			onResult?.(Result.Ok(true));
		} catch (error) {
			log.Errorf("Download failed: %j", error);
			onResult?.(Result.Err(error instanceof Error ? error : new Error("Unknown error")));
		}
	}, [fileFactory, fileName, onResult]);

	useEffect(() => {
		if (autoTrigger) {
			triggerDownload();
		}
	}, [autoTrigger, triggerDownload]);

	return triggerDownload;
}



export function UseDisableState(key: string, ms: number): [boolean, () => void] {
	const [state, setState] = hooks.UseLocalStorageState(key, false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const trigger = useCallback(() => {
		setState(true);

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			setState(false);
		}, ms);
	}, [ms, setState]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	return [state, trigger];
}


export function UseUrlParams<T extends Record<string, any>>(opts?: {
	defaultValue?: T
	stateToUrl?: <K extends keyof T>(key: K, value: T[K]) => string
	namespace?: string
	urlToState?: (value: string) => Partial<T>
	defaultKeyConverter?: (key: string) => keyof T
}) {
	const [searchParams, setSearchParams] = useSearchParams()
	const valueMapper = useRef(opts?.stateToUrl)
	const keyMap = useRef(new Map<string, keyof T>())
	const defaultValueRef = useRef(opts?.defaultValue)
	const [params, setParamsState] = useState<T>(() => extractParams(searchParams))

	function extractParams(params: URLSearchParams): T {
		const result = {} as T
		const namespace = opts?.namespace

		for (const [key, value] of params.entries()) {
			if (!namespace || key.HasPrefix(`${namespace}.`)) {
				let realKey: keyof T
				const mappedKey = keyMap.current.get(key)

				if (mappedKey) {
					realKey = mappedKey
				} else if (namespace && key.HasPrefix(`${namespace}.`)) {
					const baseKey = key.substring(namespace.length + 1)
					realKey = opts?.defaultKeyConverter?.(baseKey) ?? (baseKey as keyof T)
				} else {
					continue
				}

				if (opts?.urlToState) {
					Object.assign(result, opts.urlToState(value))
				} else {
					result[realKey] = value as T[keyof T]
				}
			}
		}

		return result
	}

	const removeNamespace = useCallback(
		(base: keyof T): string => {
			const baseStr = String(base)
			if (
				baseStr.Contains(".") &&
				opts?.namespace &&
				baseStr.HasPrefix(opts.namespace + ".")
			) {
				return baseStr.substring(opts.namespace.length + 1)
			}
			return baseStr
		},
		[opts?.namespace]
	)

	const createNamespacedKey = useCallback(
		(base: string): string => {
			if (opts?.namespace && !base.HasPrefix(opts.namespace + ".")) {
				return `${opts.namespace}.${base}`
			}
			return base
		},
		[opts?.namespace]
	)

	const mapValue = useCallback(<K extends keyof T>(key: K, value: T[K] | null): string => {
		if (valueMapper.current && !isNil(value)) {
			return valueMapper.current(key, value)
		}
		return value?.toString() || ""
	}, [])

	const updateParam = useCallback(
		(key: keyof T, value: T[keyof T] | null, params: URLSearchParams) => {
			const namespacedKey = createNamespacedKey(removeNamespace(key))
			const val = mapValue(key, value)

			if (!val || val === "") {
				params.delete(namespacedKey)
				keyMap.current.delete(namespacedKey)
			} else {
				params.set(namespacedKey, val)
				keyMap.current.set(namespacedKey, key)
			}
		},
		[createNamespacedKey, removeNamespace, mapValue]
	)

	const pendingUpdates = useRef<Partial<T>>({})
	const updateTimer = useRef<NodeJS.Timeout | null>(null)

	const setParams = useCallback(
		(updates: Partial<T>) => {
			Object.assign(pendingUpdates.current, updates)
			if (updateTimer.current) clearTimeout(updateTimer.current)

			updateTimer.current = setTimeout(() => {
				const newParams = new URLSearchParams(searchParams)
				Object.entries(pendingUpdates.current).forEach!(([key, value]) => {
					updateParam(key as keyof T, value as T[keyof T], newParams)
				})
				setSearchParams(newParams)
				pendingUpdates.current = {}
			}, 100)
		},
		[searchParams, updateParam, setSearchParams]
	)

	const resetParams = useCallback(() => {
		if (updateTimer.current) clearTimeout(updateTimer.current)
		pendingUpdates.current = {}

		const current = new URLSearchParams(window.location.search)
		const next = new URLSearchParams()
		const ns = opts?.namespace

		for (const [key, value] of current.entries()) {
			if (ns && key.HasPrefix(`${ns}.`)) continue
			next.set(key, value)
		}

		if (defaultValueRef.current) {
			Object.entries(defaultValueRef.current).forEach!(([key, value]) => {
				const namespacedKey = createNamespacedKey(key)
				const val = mapValue(key as keyof T, value)
				if (val) next.set(namespacedKey, val)
			})
		}

		setSearchParams(next)
	}, [opts?.namespace, createNamespacedKey, mapValue])

	useEffect(() => {
		setParamsState(extractParams(searchParams))
	}, [searchParams])

	useEffect(() => {
		if (defaultValueRef.current) {
			const initialParams = new URLSearchParams(window.location.search)
			let changed = false

			Object.entries(defaultValueRef.current).forEach!(([key, value]) => {
				const namespacedKey = createNamespacedKey(key)
				if (!initialParams.has(namespacedKey)) {
					initialParams.set(namespacedKey, mapValue(key as keyof T, value))
					changed = true
				}
			})

			if (changed) {
				setSearchParams(initialParams)
			}
		}
	}, [])

	useEffect(() => {
		return () => {
			if (updateTimer.current) clearTimeout(updateTimer.current)
		}
	}, [])

	return [params, setParams, resetParams] as const
}
